import { type CSSProperties } from 'react';
import { Select as SelectPrimitive } from 'radix-ui';

import { cn } from '../../lib/utils';
import { SvgExpandArrow } from '../../icons';

import { Menu } from '../ui/menu';

function isValueOption<Value>(
  option: readonly [Value, string] | typeof Menu.line,
): option is [Value, string] {
  return option !== Menu.line;
}

export type SelectOption<Value = string> = [Value, string] | typeof Menu.line;

type SelectProps<Value> = {
  id?: string;
  bare?: boolean;
  options: Array<readonly [Value, string] | typeof Menu.line>;
  value: Value;
  defaultLabel?: string;
  onChange?: (newValue: Value) => void;
  disabled?: boolean;
  disabledKeys?: Value[];
  style?: CSSProperties;
  popoverStyle?: CSSProperties;
  className?: string;
};

/**
 * @param {Array<[string, string]>} options - An array of options value-label pairs.
 * @param {string} value - The currently selected option value.
 * @param {string} [defaultLabel] - The label to display when the selected value is not in the options.
 * @param {function} [onChange] - A callback function invoked when the selected value changes.
 * @param {CSSProperties} [style] - Custom styles to apply to the selected button.
 * @param {string[]} [disabledKeys] - An array of option values to disable.
 *
 * @example
 * // Usage:
 * // <Select options={[['1', 'Option 1'], ['2', 'Option 2']]} value="1" onChange={handleOnChange} />
 * // <Select options={[['1', 'Option 1'], ['2', 'Option 2']]} value="3" defaultLabel="Select an option"  onChange={handleOnChange} />
 */
export function Select<const Value = string>({
  id,
  bare,
  options,
  value,
  defaultLabel = '',
  onChange,
  disabled = false,
  disabledKeys = [],
  style = {},
  popoverStyle = {},
  className,
}: SelectProps<Value>) {
  const valueOptions = options.filter(isValueOption);
  const valueStr = String(value);

  const handleValueChange = (str: string) => {
    const option = valueOptions.find(([v]) => String(v) === str);
    if (option) {
      onChange?.(option[0]);
    }
  };

  const triggerClasses = bare
    ? 'inline-flex items-center gap-1.5 rounded-md p-1 border-0 bg-transparent text-foreground text-[13px] leading-none cursor-pointer hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed'
    : 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 border border-input bg-background text-foreground text-[13px] leading-none cursor-pointer hover:bg-accent hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <SelectPrimitive.Root
      value={valueStr}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        className={cn(triggerClasses, className)}
        style={style}
      >
        <span
          style={{
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
        >
          <SelectPrimitive.Value placeholder={defaultLabel} />
        </span>
        <SvgExpandArrow
          style={{ width: 7, height: 7, color: 'inherit', flexShrink: 0 }}
        />
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-[9999] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-md"
          style={popoverStyle}
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option, i) => {
              if (option === Menu.line) {
                return (
                  <SelectPrimitive.Separator
                    key={i}
                    className="my-1 h-px bg-border"
                  />
                );
              }
              const [optValue, label] = option;
              return (
                <SelectPrimitive.Item
                  key={String(optValue)}
                  value={String(optValue)}
                  disabled={disabledKeys.includes(optValue)}
                  className={cn(
                    'relative flex cursor-default select-none items-center rounded-md px-2.5 py-1.5 text-[13px] outline-none',
                    'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
                    'data-[state=checked]:font-bold',
                    'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                  )}
                >
                  <SelectPrimitive.ItemText>{label}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              );
            })}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
